import React, { useEffect, useState } from 'react';
import { Box, Snackbar, Tab, Tabs } from '@mui/material';
import { useParams } from 'react-router-dom';
import * as Yup from 'yup';
import Mock from '../../constants/Mock';
import {
  IDepotAlias,
  IDepotAliasCreatePayload,
  IDepotEmailRecipient,
  IDepotEmailRecipientCreatePayload,
  IDepotEmailRecipientUpdatePayload,
} from '../../interfaces/Depot.interface';
import {
  IColumnTable,
  IDepotAliasDataTable,
  IDepotEmailRecipientDataTable,
} from '../../interfaces/Table.interface';
import {
  IFormData,
  IFormSchema,
  IInitialValues,
} from '../../interfaces/Form-Modal.interface';
import { IPagination } from '../../interfaces/Pagination.interface';
import AddEditModal from '../../shared/components/add-edit-modal/Add-Edit-Modal';
import Loader from '../../shared/components/Loader/Loader';
import Stats from '../../shared/components/stats/Stats';
import { useTranslation } from 'react-i18next';
import ButtonComponent from '../../shared/components/button/Button';
import IconStatusRejected from './../../assets/icons/icon-status-rejected.svg';
import IconCross from './../../assets/icons/icon-cross.svg';
import { useSnackbar } from '../../services/SnackbarUtils';
import {
  createDepotAlias,
  createDepotEmailRecipient,
  deleteDepotAlias,
  deleteDepotEmailRecipient,
  editDepotEmailRecipient,
  getDepotAliasList,
  getDepotEmailRecipientList,
} from '../../services/Depot.service';
import ConfirmDialog from '../../shared/components/confirm-dialog/Confirm-Dialog';
import './DepotDetails.scss';

const depotAliasFormSchema: IFormSchema = Yup.object({
  depot_name: Yup.string().required(),
  depot_code: Yup.string().required(),
});

const depotAliasFormInitialValues: IInitialValues = {
  depot_name: '',
  depot_code: '',
};

const emailRecipientFormSchema: IFormSchema = Yup.object({
  email: Yup.string().email().required(),
});

enum DepotDetailsTab {
  ALIASES = 0,
  EMAIL_RECIPIENTS = 1,
}

const DepotDetails = (): JSX.Element => {
  const { t } = useTranslation('common');
  const params = useParams();

  // UI State
  const [openModal, setOpenModal] = useState(false);
  const [openDeleteConfirmModal, setOpenDeleteConfirmModal] = useState(false);
  const [openEmailRecipientModal, setOpenEmailRecipientModal] = useState(false);
  const [openDeleteEmailRecipientConfirmModal, setOpenDeleteEmailRecipientConfirmModal] =
    useState(false);
  const [isRequestInProgress, setIsRequestInProgress] = useState<boolean>();
  const [activeTab, setActiveTab] = useState<DepotDetailsTab>(
    DepotDetailsTab.ALIASES,
  );

  const {
    snackBarOpen,
    snackbarMessage,
    snackBarType,
    showSnackbar,
    handleSnackBarClose,
  } = useSnackbar();

  const [depotsSearchValue, _setDepotsSearchValue] = useState('');
  const [depotsDataTable, setDepotsDataTable] = useState<IDepotAliasDataTable[]>([]);
  const [depotsListPagination, setDepotsListPagination] = useState<IPagination>();
  const [selectedDepotId, setSelectedDepotId] = useState<string | null | undefined>(
    null,
  );

  const [emailRecipientsSearchValue, _setEmailRecipientsSearchValue] = useState('');
  const [emailRecipientsDataTable, setEmailRecipientsDataTable] = useState<
    IDepotEmailRecipientDataTable[]
  >([]);
  const [emailRecipientsListPagination, setEmailRecipientsListPagination] =
    useState<IPagination>();
  const [selectedEmailRecipient, setSelectedEmailRecipient] =
    useState<IDepotEmailRecipient | null>(null);

  const { DepotAliasModalForm, EmailRecipientModalForm } = Mock();
  const [DepotAliasForm] = useState<IFormData[]>(
    JSON.parse(JSON.stringify(DepotAliasModalForm)),
  );
  const [DepotEmailRecipientForm] = useState<IFormData[]>(
    JSON.parse(JSON.stringify(EmailRecipientModalForm)),
  );

  const columnsTable: IColumnTable[] = [
    { accessorKey: 'name', header: t('depot_name') },
    { accessorKey: 'code', header: 'Code' },
    {
      accessorKey: 'action',
      header: t('action'),
      Cell: ({ row }: any) => {
        return (
          <div className="status-container">
            <ButtonComponent
              iconUrl={IconStatusRejected}
              iconAlt="icon-delete"
              iconOnly={true}
              iconRound={true}
              label="Delete"
              color="default"
              onClick={() => {
                handleDeleteDepot(row.original.id);
              }}
            />
          </div>
        );
      },
    },
  ];

  const emailRecipientColumnsTable: IColumnTable[] = [
    { accessorKey: 'email', header: t('email') },
    {
      accessorKey: 'action',
      header: t('action'),
      Cell: ({ row }: any) => {
        return (
          <div className="status-container">
            <ButtonComponent
              iconUrl={IconStatusRejected}
              iconAlt="icon-delete"
              iconOnly={true}
              iconRound={true}
              label="Delete"
              color="default"
              onClick={() => {
                handleDeleteEmailRecipient(row.original.id, row.original.email);
              }}
            />
          </div>
        );
      },
    },
  ];

  const fetchDepotAliasList = (offset: number): void => {
    setIsRequestInProgress(true);
    getDepotAliasList(params.id as string, 10, offset)
      .then((depotsList) => {
        setIsRequestInProgress(false);
        setDepotsListPagination(depotsList.pagination);
        getDepotsDataTable(depotsList.data);
      })
      .catch((error) => {
        setIsRequestInProgress(false);
        showSnackbar(error.message, 'error');
      });
  };

  const fetchDepotEmailRecipientList = (offset: number): void => {
    setIsRequestInProgress(true);
    getDepotEmailRecipientList(params.id as string, 10, offset)
      .then((emailRecipientList) => {
        setIsRequestInProgress(false);
        setEmailRecipientsListPagination(emailRecipientList.pagination);
        getEmailRecipientsDataTable(emailRecipientList.data);
      })
      .catch((error) => {
        setIsRequestInProgress(false);
        showSnackbar(error.message, 'error');
      });
  };

  const handleCreateDepot = (formData: IFormSchema): void => {
    setIsRequestInProgress(true);
    const depotPayload: IDepotAliasCreatePayload = {
      depot_id: params.id as string,
      name: formData.depot_name,
      code: formData.depot_code,
    };

    createDepotAlias(depotPayload)
      .then(() => {
        setIsRequestInProgress(false);
        setOpenModal(false);
        showSnackbar(t('depot_alias_has_been_created_successfully'), 'success');
        fetchDepotAliasList(0);
      })
      .catch((error) => {
        setIsRequestInProgress(false);
        showSnackbar(error.message, 'error');
      });
  };

  const handleEmailRecipientSubmit = (formData: IFormSchema): void => {
    setIsRequestInProgress(true);

    if (selectedEmailRecipient !== null) {
      const payload: IDepotEmailRecipientUpdatePayload = {
        email: formData.email,
      };

      editDepotEmailRecipient(selectedEmailRecipient.id, payload)
        .then(() => {
          setIsRequestInProgress(false);
          setOpenEmailRecipientModal(false);
          setSelectedEmailRecipient(null);
          showSnackbar(
            t('email_recipient_has_been_edited_successfully'),
            'success',
          );
          fetchDepotEmailRecipientList(0);
        })
        .catch((error) => {
          setIsRequestInProgress(false);
          showSnackbar(error.message, 'error');
        });

      return;
    }

    const payload: IDepotEmailRecipientCreatePayload = {
      depot_id: params.id as string,
      email: formData.email,
    };

    createDepotEmailRecipient(payload)
      .then(() => {
        setIsRequestInProgress(false);
        setOpenEmailRecipientModal(false);
        showSnackbar(
          t('email_recipient_has_been_created_successfully'),
          'success',
        );
        fetchDepotEmailRecipientList(0);
      })
      .catch((error) => {
        setIsRequestInProgress(false);
        showSnackbar(error.message, 'error');
      });
  };

  const handleTablePaginationChange = (tablePageIndex: number): void => {
    const offset = tablePageIndex * 10;
    fetchDepotAliasList(offset);
  };

  const handleEmailRecipientTablePaginationChange = (
    tablePageIndex: number,
  ): void => {
    const offset = tablePageIndex * 10;
    fetchDepotEmailRecipientList(offset);
  };

  const getDepotsDataTable = (depotsData: IDepotAlias[]): void => {
    const depotsDataTable: IDepotAliasDataTable[] = depotsData.map((depot) => ({
      id: depot.id,
      name: depot.name,
      code: depot.code,
    }));

    setDepotsDataTable(depotsDataTable);
  };

  const getEmailRecipientsDataTable = (
    emailRecipientsData: IDepotEmailRecipient[],
  ): void => {
    const emailRecipientsTable: IDepotEmailRecipientDataTable[] =
      emailRecipientsData.map((recipient) => ({
        id: recipient.id,
        email: recipient.email,
      }));

    setEmailRecipientsDataTable(emailRecipientsTable);
  };

  const openAddDepotModal = (): void => {
    setOpenModal(true);
  };

  const openAddEmailRecipientModal = (): void => {
    setSelectedEmailRecipient(null);
    setOpenEmailRecipientModal(true);
  };

  const openEditEmailRecipientModal = (id: string, email: string): void => {
    setSelectedEmailRecipient({ id, email });
    setOpenEmailRecipientModal(true);
  };

  const modalClose = (): void => {
    setOpenModal(false);
  };

  const emailRecipientModalClose = (): void => {
    setOpenEmailRecipientModal(false);
    setSelectedEmailRecipient(null);
  };

  const depotsRowClicked = (): void => {};
  const emailRecipientsRowClicked = (): void => {};

  const handleDeleteDepot = (depotId?: string): void => {
    setSelectedDepotId(depotId);
    setOpenDeleteConfirmModal(true);
  };

  const handleDeleteEmailRecipient = (id: string, email: string): void => {
    setSelectedEmailRecipient({ id, email });
    setOpenDeleteEmailRecipientConfirmModal(true);
  };

  useEffect(() => {
    fetchDepotAliasList(0);
    fetchDepotEmailRecipientList(0);
  }, []);

  const handleTabChange = (
    _event: React.SyntheticEvent,
    tabIndex: DepotDetailsTab,
  ): void => {
    setActiveTab(tabIndex);
  };

  const setDepotsHeaderButtons = (): React.JSX.Element => {
    return (
      <>
        <ButtonComponent
          iconUrl={IconCross}
          iconAlt="icon-cross"
          label={t('add_depot_alias')}
          color="primary"
          onClick={openAddDepotModal}
        />
      </>
    );
  };

  const setEmailRecipientHeaderButtons = (): React.JSX.Element => {
    return (
      <>
        <ButtonComponent
          iconUrl={IconCross}
          iconAlt="icon-cross"
          label={t('add_email_recipient')}
          color="primary"
          onClick={openAddEmailRecipientModal}
        />
      </>
    );
  };

  const getActiveTitle = (): string => {
    return activeTab === DepotDetailsTab.ALIASES
      ? t('depot_alias')
      : t('email_recipients');
  };

  const getActiveHeaderButton = (): React.JSX.Element => {
    return activeTab === DepotDetailsTab.ALIASES
      ? setDepotsHeaderButtons()
      : setEmailRecipientHeaderButtons();
  };

  const getActiveEmptyMessage = (): string => {
    if (isRequestInProgress !== false) {
      return '';
    }

    return activeTab === DepotDetailsTab.ALIASES
      ? t('no_depots_alias_to_display')
      : `${String(t('no_email_recipients_to_display'))} ${String(
          t('email_recipient_empty_fallback_warning'),
        )}`;
  };

  const getActivePaginationTotalPages = (): number => {
    if (activeTab === DepotDetailsTab.ALIASES) {
      return depotsListPagination !== undefined ? depotsListPagination.totalPages : 0;
    }

    return emailRecipientsListPagination !== undefined
      ? emailRecipientsListPagination.totalPages
      : 0;
  };

  const renderDepotTabs = (): React.JSX.Element => {
    return (
      <Box className="depot-details-tabs">
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label="depot configuration tabs"
        >
          <Tab label={t('depot_alias')} />
          <Tab label={t('email_recipients')} />
        </Tabs>
        {getActiveHeaderButton()}
      </Box>
    );
  };

  return (
    <div className="depot-details">
      {isRequestInProgress === true && <Loader />}

      <Stats
        displayHeaderLeftSideBlock={true}
        title={getActiveTitle()}
        searchValue={
          activeTab === DepotDetailsTab.ALIASES
            ? depotsSearchValue
            : emailRecipientsSearchValue
        }
        headerBottomContent={renderDepotTabs()}
        dataTable={
          activeTab === DepotDetailsTab.ALIASES
            ? depotsDataTable
            : emailRecipientsDataTable
        }
        columnsTable={
          activeTab === DepotDetailsTab.ALIASES
            ? columnsTable
            : emailRecipientColumnsTable
        }
        emptyMsgTable={getActiveEmptyMessage()}
        onTableRowClicked={
          activeTab === DepotDetailsTab.ALIASES
            ? depotsRowClicked
            : emailRecipientsRowClicked
        }
        handleTablePaginationChange={
          activeTab === DepotDetailsTab.ALIASES
            ? handleTablePaginationChange
            : handleEmailRecipientTablePaginationChange
        }
        tableTotalPages={getActivePaginationTotalPages()}
        searchPlaceholder={
          activeTab === DepotDetailsTab.ALIASES
            ? t('depot_alias_search_placeholder')
            : t('email_recipients_search_placeholder')
        }
      />

      <Snackbar
        className={`oryx-snackbar ${snackBarType}`}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={snackBarOpen}
        message={snackbarMessage}
        autoHideDuration={3000}
        onClose={handleSnackBarClose}
      />

      <AddEditModal
        modalTitle={t('add_a_depot_alias')}
        modalForm={DepotAliasForm}
        formSchema={depotAliasFormSchema}
        formInitialValues={depotAliasFormInitialValues}
        openModal={openModal}
        onClose={modalClose}
        handleSubmit={handleCreateDepot}
        closeConfirmationModalTitle={t('are_you_sure')}
        closeConfirmationModalDescription={t('depot_alias_info_will_be_lost')}
      />

      <AddEditModal
        modalTitle={
          selectedEmailRecipient !== null
            ? t('edit_email_recipient')
            : t('add_email_recipient')
        }
        modalForm={DepotEmailRecipientForm}
        formSchema={emailRecipientFormSchema}
        formInitialValues={{
          email:
            selectedEmailRecipient !== null ? selectedEmailRecipient.email : '',
        }}
        openModal={openEmailRecipientModal}
        onClose={emailRecipientModalClose}
        handleSubmit={handleEmailRecipientSubmit}
        closeConfirmationModalTitle={t('are_you_sure')}
        closeConfirmationModalDescription={t('email_recipient_info_will_be_lost')}
      />

      <ConfirmDialog
        openModal={openDeleteConfirmModal}
        title={t('depot_alias_delete_modal_title')}
        description={t('depot_alias_delete_modal_description')}
        confirmLabel={t('depot_alias_delete_modal_label')}
        cancelLabel={t('cancel')}
        confirmButtonColor={'inactive'}
        handleConfirm={() => {
          if (selectedDepotId !== undefined && selectedDepotId !== null) {
            void deleteDepotAlias(selectedDepotId);
            fetchDepotAliasList(0);
          }
          setOpenDeleteConfirmModal(false);
        }}
        handleClose={() => {
          setOpenDeleteConfirmModal(false);
        }}
      />

      <ConfirmDialog
        openModal={openDeleteEmailRecipientConfirmModal}
        title={t('email_recipient_delete_modal_title')}
        description={t('email_recipient_delete_modal_description')}
        confirmLabel={t('email_recipient_delete_modal_label')}
        cancelLabel={t('cancel')}
        confirmButtonColor={'inactive'}
        handleConfirm={() => {
          if (selectedEmailRecipient !== null) {
            setIsRequestInProgress(true);
            void deleteDepotEmailRecipient(selectedEmailRecipient.id)
              .then(() => {
                setIsRequestInProgress(false);
                showSnackbar(
                  t('email_recipient_has_been_deleted_successfully'),
                  'success',
                );
                fetchDepotEmailRecipientList(0);
              })
              .catch((error) => {
                setIsRequestInProgress(false);
                showSnackbar(error.message, 'error');
              });
          }
          setOpenDeleteEmailRecipientConfirmModal(false);
          setSelectedEmailRecipient(null);
        }}
        handleClose={() => {
          setOpenDeleteEmailRecipientConfirmModal(false);
          setSelectedEmailRecipient(null);
        }}
      />
    </div>
  );
};

export default DepotDetails;
